import { ImmutableArray } from "@hookstate/core";
import { Account, Release, ServerSingleResponse, Bundle } from "../../types";
import { FUNNEL_TYPES } from "./utils/contants";
import { AxiosResponse } from "axios";
import { AuthType } from "../../hooks/useAuthMutations";

export type FunnelOnSuccessResult = {
  lastFunnelType?: FUNNEL_TYPES;
  nextFunnelType?: FUNNEL_TYPES;
  authMethod?: AuthType;
  data?: AuthInvalidationTypes;
  context?: FunnelContextTypes;
  options?: FunnelOptionsTypes;
};

export type FunnelOnSuccess = (result: FunnelOnSuccessResult) => void;

export type FunnelComponentProps = {
  onClose?: () => void;
  onSuccess: FunnelOnSuccess;
  sendEvent?: (event: any) => void;
  setFunnelType: (type: FUNNEL_TYPES) => void;
  updateFunnelConfig: (config: FunnelConfigTypes) => void;
  isModal?: boolean;
  modalContext?: FunnelContextArea;
  release?: Release;
  artistId?: number;
  cacheKey?: string;
  shouldShowSteps?: boolean;
  stepCount?: number;
  currentStep?: number;
  stepText?: string;
  stepDisplayText?: string;
  showStepIndicators?: boolean;
};

export enum FunnelContextArea {
  // Default context for generic funnel usage - always use this as fallback
  default = "default",
  reader = "reader",
  verticalReader = "vertical-reader",
  readerPreviewBanner = "reader-preview-banner",
  navBar = "nav-bar",
  followArtist = "follow-artist",
  addToLibrary = "add-to-library",
  reaction = "reaction",
  donate = "donate",
  purchasePdf = "purchase-pdf",
  comment = "comment",
  settings = "settings",
  gold = "gold",
  gift = "gift",
  startNextReleaseButton = "start_next_release_button",
  seriesInfo = "series-info",
  chatSchedule = "chat-schedule",
  globalChat = "global-chat",
  releasesList = "releases-list",
  releaseDetails = "release-details",
  ppbPurchase = "ppb-purchase",
  newsComments = "news-comments",
  comicDetails = "comic-details",
  offerPopUp = "offer-pop-up",
  subscription = "subscription",
  valkyrae = "valkyrae",
  blackFridayDeal = "black-friday-deal",
  holidayBundle = "holiday-bundle",
  readerOverlay = "reader-splash",
  endOfRelease = "end-of-release",
  chat = "chat",
  news = "news",
  notifications = "notifications",
  featureCard = "feature-card",
  heroCard = "hero-card",
  offerPaywall = "offer-paywall",
}

export enum ContextReferrer {
  settings = "settings",
  sidebar = "sidebar",
}

// Event context areas for analytics tracking - aligned with mobile app
// Used specifically for navigationContext in payment funnels
export enum EventContextArea {
  // Mobile app contexts - keep aligned
  reader = "reader",
  offerPopUp = "offerPopUp",
  settings = "settings",
  releaseDetails = "releaseDetails",
  unlockableReleaseCard = "unlockableReleaseCard",
  featureCard = "featureCard",
  previewBanner = "previewBanner",
  offerPaywall = "offerPaywall",

  // Web-specific contexts
  goldPage = "goldPage",
  valkyraePage = "valkyraePage",
  blackFridayPage = "blackFridayPage",
  blackFridayPageExpress = "blackFridayPageExpress",
  chatSchedule = "chatSchedule",
  pdfPurchase = "pdfPurchase",
}

export function funnelToEventContextArea(
  modalContext?: FunnelContextArea,
  fallback: EventContextArea = EventContextArea.releaseDetails,
): EventContextArea {
  switch (modalContext) {
    case FunnelContextArea.reader:
    case FunnelContextArea.verticalReader:
    case FunnelContextArea.readerOverlay:
    case FunnelContextArea.readerPreviewBanner:
      return EventContextArea.reader;
    case FunnelContextArea.gold:
      return EventContextArea.goldPage;
    case FunnelContextArea.releaseDetails:
      return EventContextArea.releaseDetails;
    case FunnelContextArea.featureCard:
      return EventContextArea.featureCard;
    case FunnelContextArea.offerPopUp:
      return EventContextArea.offerPopUp;
    case FunnelContextArea.settings:
      return EventContextArea.settings;
    default:
      return fallback;
  }
}

export interface FunnelNavigationContext {
  contextArea: EventContextArea;
  contextReferrer?: ContextReferrer;
  data?: {
    comicId?: number;
    releaseId?: number;
    releaseOrder?: number;
    comicName?: string;
    artistId?: number;
    ppbPricePoint?: number;
    ppbGoldPricePoint?: number;
    hasSubscribeOffer?: boolean;
    premiumOnly?: number;
  };
}

export type FunnelContextTypes = {
  modalContext?: FunnelContextArea;
  navigationContext?: FunnelNavigationContext;
  donationArtistId?: number;
  purchaseRelease?: Release;
  bundle?: Bundle;
  cacheKey?: string;
};

export type FunnelConfigTypes = {
  type?: FUNNEL_TYPES;
  context?: FunnelContextTypes;
  options?: FunnelOptionsTypes;
};

export interface FunnelOptionsTypes {
  valueProps?: ImmutableArray<string>;
  welcomeText?: string;
  // Trap focus does not close on click off
  trapFocus?: boolean;
  // Locked removes the close button and traps focus
  locked?: boolean;
  // If true, the funnel will be opened as a modal
  // with some components positioned differently
  isModal?: boolean;
  authMethod?: AuthType;
  didPurchaseSubscription?: boolean;
  purchasedYearly?: boolean;
  // Skip onboarding flow after signup
  skipOnboarding?: boolean;
  // Skip offer paywall modal
  skipOfferPaywallModal?: boolean;
  // Show new onboarding (archetype selection) instead of classic (genres/themes)
  // Defaults to false (classic) if not provided
  newOnboarding?: boolean;
  // Additional props to pass to the funnel component
  componentProps?: Record<string, any>;

  onClose?: () => void;
  onSuccess?: FunnelOnSuccess;
}

export type SetFunnelTypes = (config: FunnelConfigTypes) => void;

export type AuthInvalidationTypes = [
  AxiosResponse<ServerSingleResponse<Account>> | null,
  AxiosResponse<ServerSingleResponse<Release>> | undefined | null,
];
